import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState
} from 'react';

import Divider from '@material-ui/core/Divider';
import Grid from '@material-ui/core/Grid';
import Paper from '@material-ui/core/Paper';
import withWidth, { isWidthUp } from '@material-ui/core/withWidth';
import classnames from 'classnames';
import {
  every,
  filter,
  flatMap,
  flatten,
  flow,
  get,
  isNull,
  map
} from 'lodash';
import { useAssignmentDetail } from 'sdk';

import { AssignmentEditModal } from 'pages/Teachers/Assignments/Detail/components/AssignmentEditModal';
import { withNavbar } from 'pages/Teachers/shared';
import { useAllSectionsList } from 'pages/Teachers/shared/AssignmentDialog/components/AssignAssignment/sdk';
import TrackerQuestionsAndAnswersList from 'pages/Teachers/shared/TrackerQuestionsAndAnswersList';
import { colors } from 'theme/palette';
import { calendarDate } from 'utils/date';
import { useEdLightTracking } from 'utils/integrations/mixpanel';
import {
  MixpanelAction,
  MixpanelObject
} from 'utils/integrations/mixpanel/constants';
import { notifyErrors, notifySuccess } from 'utils/notifications';
import { processSections } from 'utils/sections';
import { openInNewTab } from 'utils/urls';

import Button from 'components/Button';
import Typography from 'components/Typography';

import { ReactComponent as TrashIcon } from './assets/trash.svg';
import {
  AddTeacherConfirmDialog,
  AssignmentDetailSkeleton,
  AssignmentListByInstance,
  TasksList
} from './components';
import {
  addTeacherToSections,
  bulkSoftDeleteAssignments,
  canPreviewAsStudent
} from './sdk';
import styles from './styles.module.scss';

const TeacherAssignmentDetail = forwardRef((props, ref) => {
  const { match, width, sideNav } = props;
  const trackerId = match.params.trackerId;

  const {
    data: assignmentDetails,
    loading: isLoading,
    refetch: assignmentDetailsRefetch
  } = useAssignmentDetail({ trackerId: trackerId }) || {};

  const { data: rawSectionsResponse } = useAllSectionsList();

  const { trackEvent } = useEdLightTracking({
    feature: 'edit assignment',
    view: 'assignment'
  });

  const sections = useMemo(() => {
    const actualData = Array.isArray(rawSectionsResponse)
      ? rawSectionsResponse
      : rawSectionsResponse?.data || [];

    return processSections({ data: actualData });
  }, [rawSectionsResponse]);

  const [studentPreviewStatus, setStudentPreviewStatus] = useState(null);

  const [dialogs, setDialogs] = useState({
    edit: { open: false },
    confirm: { open: false }
  });

  useEffect(() => {
    if (assignmentDetails) {
      checkCanPreviewAsStudent(assignmentDetails);
    }
  }, [assignmentDetails]);

  const checkCanPreviewAsStudent = async (data) => {
    const canPreviewResponses = await Promise.all(
      map(data.instances, ({ id }) => canPreviewAsStudent(id))
    );

    setStudentPreviewStatus(
      every(canPreviewResponses, 'data.can_preview_as_student')
    );
  };

  const softDeleteAll = async () => {
    const assignments = flatMap(assignmentDetails?.instances, 'assignments');
    const activeAssignments = filter(assignments, (x) => !x.deleted);
    const activeAssignmentsIds = map(activeAssignments, 'id');

    const { success, errors } = await bulkSoftDeleteAssignments(
      activeAssignmentsIds
    );

    trackEvent(`${MixpanelObject.FORM} ${MixpanelAction.SUBMIT}`, {
      form: 'edit assignment',
      button: 'restore class',
      assignment_ids: activeAssignmentsIds
    });

    if (success) {
      notifySuccess(
        `Successfully archived ${activeAssignmentsIds.length} assignments.`
      );
      assignmentDetailsRefetch();
    } else {
      notifyErrors(errors);
    }
  };

  const openAssignmentEditDialog = () => {
    setDialogs((prev) => ({ ...prev, edit: { open: true } }));
  };

  const closeAssignmentEditDialog = () => {
    setDialogs((prev) => ({ ...prev, edit: { open: false } }));
    assignmentDetailsRefetch();
  };

  const openConfirmDialog = () => {
    setDialogs((prev) => ({ ...prev, confirm: { open: true } }));
  };

  const closeConfirmDialog = () => {
    setDialogs((prev) => ({ ...prev, confirm: { open: false } }));
  };

  const handleStudentPreview = () => {
    if (isNull(studentPreviewStatus)) {
      return;
    }

    if (!studentPreviewStatus) {
      openConfirmDialog();
    } else {
      const deeplink = get(assignmentDetails, 'instances[0].student_deeplink');
      openInNewTab(deeplink);
    }
  };

  useImperativeHandle(ref, () => ({
    handleStudentPreview
  }));

  const ensureTeacherInSections = async () => {
    const sectionIds = flow([
      (instances) => map(instances, 'assignments'),
      flatten,
      (assignments) => map(assignments, 'section_id')
    ])(assignmentDetails?.instances);

    await addTeacherToSections(sectionIds);

    setStudentPreviewStatus(true);
    setDialogs((prev) => ({ ...prev, confirm: { open: false } }));

    const deeplink = get(assignmentDetails, 'instances[0].student_deeplink');
    openInNewTab(deeplink);
  };

  const lgScreen = isWidthUp('lg', width);
  const sideNavOpen = sideNav.open;

  const paperClassName = classnames({
    [styles.noMarginPaper]: !lgScreen,
    [styles.smallMarginPaper]: lgScreen && sideNavOpen,
    [styles.bigMarginPaper]: lgScreen && !sideNavOpen
  });

  const author = isLoading ? null : assignmentDetails?.created_by?.name;
  const instances = get(assignmentDetails, 'instances', []);
  const assignments = flatMap(instances, 'assignments');
  const allAssignmentsAreArchived = every(assignments, 'deleted');

  return (
    <>
      <Paper className={paperClassName} square={!lgScreen} variant="outlined">
        {isLoading && <AssignmentDetailSkeleton />}
        {!isLoading && assignmentDetails && (
          <>
            <Grid container alignItems="center" className={styles.paperHeading}>
              <Grid container item xs direction="column" spacing={1}>
                <Grid item>
                  <Typography variant="H-TEXT-2" color={colors.blueDark}>
                    {assignmentDetails?.name}
                  </Typography>
                </Grid>
                <Grid item>
                  <Typography variant="B-Text-3" color={colors.grey1}>
                    {author} created{' '}
                    {calendarDate(assignmentDetails?.created_at)}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item>
                <Button
                  color="pink"
                  variant="medium"
                  onClick={openAssignmentEditDialog}
                >
                  Edit
                </Button>
              </Grid>
            </Grid>
            <Divider />

            <div className={styles.paperSection}>
              <div className={styles.assignedSection}>
                <Typography variant="H-TEXT-3" color={colors.blueDark}>
                  Assigned
                </Typography>
                <div>
                  <Button
                    color="lightGrey"
                    variant="small"
                    startIcon={<TrashIcon className={styles.trashIcon} />}
                    disabled={allAssignmentsAreArchived}
                    onClick={softDeleteAll}
                  >
                    Archive all
                  </Button>
                </div>
              </div>
              <AssignmentListByInstance assignmentDetails={assignmentDetails} />
            </div>

            <Divider />

            <div className={styles.paperSection}>
              <Grid
                container
                alignItems="center"
                justifyContent="space-between"
              >
                <Grid item>
                  <Typography variant="H-TEXT-3" color={colors.blueDark}>
                    Tasks
                  </Typography>
                </Grid>
              </Grid>

              <TasksList tasks={assignmentDetails?.tasks} />
            </div>

            {!assignmentDetails?.assignment_template_id &&
              assignmentDetails?.has_only_math_standards && (
                <>
                  <Divider />
                  <div className={styles.transcriptionsAndSolutions}>
                    <Typography variant="H-TEXT-3" color={colors.blueDark}>
                      Questions and Answers per page
                    </Typography>
                    <TrackerQuestionsAndAnswersList
                      trackerId={assignmentDetails?.id}
                    />
                  </div>
                </>
              )}
            <Divider />
          </>
        )}
      </Paper>

      {dialogs.edit.open && (
        <AssignmentEditModal
          open={dialogs.edit.open}
          sections={sections}
          assignmentDetails={assignmentDetails}
          assignmentDetailsRefetch={assignmentDetailsRefetch}
          onClose={closeAssignmentEditDialog}
        />
      )}

      {dialogs.confirm.open && (
        <AddTeacherConfirmDialog
          onClose={closeConfirmDialog}
          onConfirm={ensureTeacherInSections}
        />
      )}
    </>
  );
});

TeacherAssignmentDetail.displayName = 'TeacherAssignmentDetail';

export default withWidth()(
  withNavbar(TeacherAssignmentDetail, { title: 'Manage assignment' })
);
